// src/components/Navbar.js
import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { I18nContext } from "../App";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, logoutUser } from "../utils/authUtils";
import { db } from "../config/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import Logo from "./Logo";
import { logEvent } from "../utils/analytics";
import { getPreferredTheme, persistTheme } from "../utils/theme";
import { resolveRetailerIdForUser } from "../utils/retailerIdentity";
import { fetchSelfRetailerProfile } from "../utils/profileApi";
import {
  BsList,
  BsX,
  BsChevronDown,
  BsBoxArrowRight,
  BsHouse,
  BsArrowUp,
  BsGlobe,
  BsSun,
  BsMoonStars,
  BsTablet
} from "react-icons/bs";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";

// Feature flag: toggle language switch UI (auto-detection remains active via App.js)
const SHOW_LANGUAGE_SWITCH =
  process.env.REACT_APP_ENABLE_I18N_SWITCH === "true";

/**
 * LanguageSelector
 * Self-contained selector with persistence and global event.
 * Props:
 *  - variant: "desktop" | "mobile"
 *  - className?: string
 */
const getDefaultLocale = () => {
  try {
    const saved = localStorage.getItem("zedi_locale");
    if (saved) return saved;
    const navigatorLocale = (navigator.language || "en").slice(0, 2);
    return navigatorLocale;
  } catch {
    return "en";
  }
};

const LanguageSelector = ({ variant = "desktop", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [fallbackLocale, setFallbackLocale] = useState(getDefaultLocale);
  const menuRef = useRef(null);
  const i18n = useContext(I18nContext);
  const currentLocale = i18n?.locale ?? fallbackLocale;
  const isRTL = ["ar", "yi"].includes(currentLocale);

  const options = [
    { label: "English", code: "en" },
    { label: "Français", code: "fr" },
    { label: "Deutsch", code: "de" },
    { label: "Español", code: "es" },
    { label: "Italiano", code: "it" },
    { label: "Português", code: "pt" },
    { label: "Ελληνικά", code: "el" },
    { label: "中文", code: "zh" },
    { label: "日本語", code: "ja" },
    { label: "한국어", code: "ko" },
    { label: "বাংলা", code: "bn" },
    { label: "العربية", code: "ar" },
    { label: "ייִדיש", code: "yi" }
  ];

  const applyLocale = useCallback(
    (code) => {
      if (i18n && typeof i18n.setLocale === "function") {
        i18n.setLocale(code);
      } else {
        setFallbackLocale(code);
        try {
          localStorage.setItem("zedi_locale", code);
        } catch {}
      }
      try {
        window.dispatchEvent(
          new CustomEvent("zedipass:language-change", { detail: { locale: code } })
        );
      } catch {}
      setOpen(false);
    },
    [i18n]
  );

  const toggleOpen = useCallback(() => {
    logEvent("language_click", { method: variant });
    setOpen((v) => !v);
  }, [variant]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (variant === "mobile") {
    return (
      <div
        className={`mt-4 ${className}`}
        // Prevent backdrop/menu handlers from stealing the event
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        role="group"
        aria-label="Language selector"
      >
        <label htmlFor="mobile-language" className="sr-only">
          Language
        </label>
        <select
          id="mobile-language"
          value={currentLocale}
          onChange={(e) => {
            const code = e.target.value;
            try { console.log("🌐 Language changed (mobile):", code); } catch {}
            applyLocale(code);
          }}
          className="w-full text-lg bg-white text-gray-900 dark:bg-gray-900 dark:text-white border border-gray-400 dark:border-gray-600 rounded-full px-5 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-label="Select language"
          dir={isRTL ? "rtl" : "ltr"}
          data-testid="language-select-mobile"
        >
          {options.map((opt) => (
            <option key={opt.code} value={opt.code} lang={opt.code}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Desktop: globe icon with dropdown
  return (
    <div
      className={`relative ${className}`}
      ref={menuRef}
      data-testid="language-selector"
    >
      <button
        onClick={toggleOpen}
        className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 text-gray-700 dark:text-white"
        aria-label="Change language"
        title="Change language"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="language-menu"
        data-testid="language-toggle"
      >
        <BsGlobe className="text-xl !text-gray-700 dark:!text-white" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-52 rounded-lg shadow-lg bg-white dark:bg-gray-900 ring-1 ring-black/5 dark:ring-white/10 py-1 z-50"
            role="menu"
            id="language-menu"
            aria-label="Available languages"
          >
            {options.map((opt) => (
              <button
                key={opt.code}
                onClick={() => applyLocale(opt.code)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none focus:bg-blue-100 dark:focus:bg-gray-700 ${
                  currentLocale === opt.code
                    ? "font-semibold text-blue-700 dark:text-blue-300"
                    : "text-gray-800 dark:text-gray-200"
                }`}
                role="menuitemradio"
                aria-checked={currentLocale === opt.code}
                lang={opt.code}
                dir={["ar", "yi"].includes(opt.code) ? "rtl" : "ltr"}
                data-testid={`language-option-${opt.code}`}
              >
                {opt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ThemeModeToggle = ({ variant = "desktop", className = "" }) => {
  const [theme, setTheme] = useState(getPreferredTheme);
  const isDark = theme === "dark";

  useEffect(() => {
    persistTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      logEvent("theme_toggle", { method: variant, to: next });
      return next;
    });
  }, [variant]);

  const label = isDark ? "Switch to light mode" : "Switch to dark mode";

  const toggleTrack = (
    <span className="relative inline-flex h-9 w-[84px] items-center rounded-full border border-slate-200/90 bg-gradient-to-r from-amber-50 to-sky-50 p-1 shadow-sm transition-colors dark:border-slate-700/80 dark:from-slate-800 dark:to-slate-900">
      <motion.span
        aria-hidden="true"
        className="pointer-events-none absolute left-1 top-1 h-7 w-7 rounded-full bg-white shadow-md ring-1 ring-black/5 dark:bg-slate-700 dark:ring-white/10"
        animate={{ x: isDark ? 48 : 0, rotate: isDark ? 180 : 0 }}
        transition={{ type: "spring", stiffness: 330, damping: 24 }}
      />
      <span className="relative z-10 flex w-full items-center justify-between px-1.5">
        <BsSun
          className={`h-4 w-4 transition-colors ${
            isDark ? "text-slate-400" : "text-amber-500"
          }`}
        />
        <BsMoonStars
          className={`h-4 w-4 transition-colors ${
            isDark ? "text-indigo-300" : "text-slate-400"
          }`}
        />
      </span>
    </span>
  );

  if (variant === "mobile") {
    return (
      <div className={`mt-6 ${className}`}>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={label}
          aria-pressed={isDark}
          data-testid="theme-toggle-mobile"
          className="w-full rounded-2xl border border-white/20 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-xl transition hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-700/70 dark:bg-gray-900/85 dark:hover:bg-gray-900"
        >
          <span className="flex items-center justify-between gap-4">
            <span className="text-left">
              <span className="block text-sm font-semibold text-slate-900 dark:text-gray-100">
                Appearance
              </span>
              <span className="block text-xs text-slate-500 dark:text-gray-400">
                {isDark ? "Dark mode is on" : "Light mode is on"}
              </span>
            </span>
            {toggleTrack}
          </span>
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      aria-pressed={isDark}
      data-testid="theme-toggle"
      className={`focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 ${className}`}
      title={isDark ? "Dark mode" : "Light mode"}
    >
      {toggleTrack}
    </button>
  );
};

const Navbar = () => {
  const isPermissionDeniedError = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    return (
      code === "permission-denied" ||
      code === "firestore/permission-denied" ||
      message.includes("permission-denied") ||
      message.includes("insufficient permissions")
    );
  };

  const { t } = useContext(I18nContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [activeNavDropdown, setActiveNavDropdown] = useState(null);
  const [expandedMobileItem, setExpandedMobileItem] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [visible, setVisible] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const prevScrollPosRef = useRef(0);
  const scrollRafRef = useRef(0);
  const userDropdownRef = useRef(null);
  const logoHoldTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  // Close user dropdown on outside click
  useEffect(() => {
    if (!userDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(e.target)
      ) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userDropdownOpen]);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [businessName, setBusinessName] = useState("");
  const [logoURL, setLogoURL] = useState("");
  const [logoBackdropClass, setLogoBackdropClass] = useState("bg-white");

  // Load retailer profile data for name + logo
  useEffect(() => {
    if (!user) {
      setBusinessName("");
      setLogoURL("");
      return;
    }
    // A new Auth account is not a retailer until its email has been verified
    // and the canonical provisioner has completed. Avoid billing/profile calls
    // during that boundary so the verification page stays clean and no caller
    // can mistake the Auth UID for an already-provisioned retailer record.
    if (user.emailVerified !== true) {
      setBusinessName(user.email?.split("@")[0] || "");
      setLogoURL("");
      return;
    }
    let cancelled = false;

    (async () => {
      const retailerDocId = await resolveRetailerIdForUser({ user });
      if (cancelled) return;
      const ref = doc(db, "zedipass_retailers", retailerDocId || user.uid);
      try {
        const snap = await getDoc(ref);
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() || {}) : {};
        const bi = data.businessInsights || {};
        const basic = bi.basicInfo || {};
        const branding = bi.branding || {};
        setBusinessName(basic.businessName || data.businessName || user.email?.split("@")[0] || "");
        setLogoURL(branding.logoURL || data.logoURL || "");
      } catch (error) {
        if (isPermissionDeniedError(error)) {
          const fallbackProfile = await fetchSelfRetailerProfile(user);
          if (cancelled) return;
          const fallbackInsights = fallbackProfile?.businessInsights || {};
          const fallbackBasic = fallbackInsights.basicInfo || {};
          const fallbackBranding = fallbackInsights.branding || {};
          const fallbackPublicContact = fallbackProfile?.publicContact || {};
          setBusinessName(
            fallbackBasic.businessName ||
            fallbackProfile?.businessName ||
            user.email?.split("@")[0] ||
            ""
          );
          setLogoURL(
            fallbackBranding.logoURL ||
            fallbackProfile?.logoURL ||
            fallbackPublicContact.logoURL ||
            ""
          );
          return;
        }
        console.warn("[Navbar] Failed to subscribe to retailer profile", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Adaptive background fill for transparent logos:
  // light logos get dark fill, dark logos get white fill.
  useEffect(() => {
    let cancelled = false;
    if (!logoURL) {
      setLogoBackdropClass("bg-white");
      return undefined;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.referrerPolicy = "no-referrer";

    img.onload = () => {
      if (cancelled) return;
      try {
        const sampleSize = 48;
        const canvas = document.createElement("canvas");
        canvas.width = sampleSize;
        canvas.height = sampleSize;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
          setLogoBackdropClass("bg-white");
          return;
        }

        ctx.clearRect(0, 0, sampleSize, sampleSize);
        ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
        const { data } = ctx.getImageData(0, 0, sampleSize, sampleSize);

        let opaquePixels = 0;
        let luminanceSum = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha < 20) continue;
          const r = data[i] / 255;
          const g = data[i + 1] / 255;
          const b = data[i + 2] / 255;
          const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          luminanceSum += luminance;
          opaquePixels += 1;
        }

        if (opaquePixels === 0) {
          setLogoBackdropClass("bg-white");
          return;
        }

        const averageLuminance = luminanceSum / opaquePixels;
        setLogoBackdropClass(averageLuminance > 0.72 ? "bg-slate-900" : "bg-white");
      } catch (_) {
        // Canvas can fail on strict CORS images; default to white fill.
        setLogoBackdropClass("bg-white");
      }
    };

    img.onerror = () => {
      if (!cancelled) setLogoBackdropClass("bg-white");
    };

    img.src = logoURL;

    return () => {
      cancelled = true;
    };
  }, [logoURL]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    document.body.style.overflow = "auto";
    setExpandedMobileItem(null);
  }, []);

  const closeNavigationUI = useCallback(() => {
    closeMenu();
    setUserDropdownOpen(false);
    setActiveNavDropdown(null);
  }, [closeMenu]);

  const openQrExperience = useCallback(() => {
    setShowQrModal(true);
    logEvent("qr_experience_open", { source: "navbar" });
  }, []);

  const clearLogoHold = useCallback(() => {
    if (logoHoldTimerRef.current) {
      window.clearTimeout(logoHoldTimerRef.current);
      logoHoldTimerRef.current = null;
    }
  }, []);

  const startMobileLogoHold = useCallback(() => {
    if (window.matchMedia("(min-width: 768px)").matches) return;
    longPressTriggeredRef.current = false;
    clearLogoHold();
    logoHoldTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      openQrExperience();
    }, 2000);
  }, [clearLogoHold, openQrExperience]);

  const cancelMobileLogoHold = useCallback(() => {
    clearLogoHold();
  }, [clearLogoHold]);

  const toggleMenu = useCallback(() => {
    // Analytics: track navbar toggle open/close
    logEvent("nav_toggle", { open: !menuOpen });
    if (menuOpen) {
      closeMenu();
    } else {
      setMenuOpen(true);
      document.body.style.overflow = "hidden";
    }
  }, [menuOpen, closeMenu]);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };
  const handleTouchMove = (e) => {
    if (touchStart === null) return;
    const touchEnd = e.touches[0].clientX;
    if (touchStart - touchEnd > 50) {
      toggleMenu();
      setTouchStart(null);
    }
  };

  const handleNavigation = useCallback(
    async (to, action) => {
      // Analytics: track navbar navigation clicks
      logEvent("nav_click", {
        destination: action === "logout" ? "logout" : to,
        method: "navbar"
      });
      try {
        if (action === "logout") {
          await logoutUser();
          to = "/login";
        }
        closeNavigationUI();
        setTimeout(() => navigate(to), 300);
      // setIsNavigating(true); // removed unused navigation state
      } finally {
        // setIsNavigating(false); // removed unused navigation state
      }
    },
    [navigate, closeNavigationUI]
  );

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    const updateFromScroll = () => {
      scrollRafRef.current = 0;
      const current = window.scrollY || 0;
      const isDown = current > prevScrollPosRef.current;

      const nextVisible = current < 10 || !isDown;
      const nextShowBackToTop = current > window.innerHeight && current > 50;
      const nextHasScrolled = current > 50;

      setVisible((value) => (value === nextVisible ? value : nextVisible));
      setShowBackToTop((value) =>
        value === nextShowBackToTop ? value : nextShowBackToTop
      );
      setHasScrolled((value) => (value === nextHasScrolled ? value : nextHasScrolled));

      prevScrollPosRef.current = current;
    };

    const onScroll = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(updateFromScroll);
    };

    updateFromScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
      clearLogoHold();
    };
  }, [clearLogoHold]);

  // Theme handled by <ThemeModeToggle />

  const navItems = [
    { name: t("nav.home"), path: "/", icon: <BsHouse className="mr-1" /> },
    { name: t("nav.product"), path: "/product/" },
    { name: t("nav.findyourfit"), path: "/findyourfit" },
    // { name: t("nav.businessShowcase"), path: "/business-showcase" },
    { name: t("nav.pricing"), path: "/pricing" },
    { name: "Our Story", path: "/our-story" },
    /*
    {
      name: t("nav.solutions"),
      subItems: [
        { name: t("nav.solutions.accessibility"), path: "/solutions?tab=accessibility" },
        { name: t("nav.solutions.smallBusiness"), path: "/solutions?tab=smallBusiness" },
        { name: t("nav.solutions.community"), path: "/solutions?tab=community" }
      ]
    }
    */
    // Desktop shows a separate icon CTA next to the account section.
  ];

  return (
    <>
      {/* Desktop Navbar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{
          y: visible ? 0 : -100,
          transition: { duration: 0.3, ease: "easeInOut" }
        }}
        className="fixed top-0 left-0 w-full h-16 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            <Link
              to="/"
              aria-label="ZediPass home"
              className="flex items-center"
              onClick={(e) => {
                if (longPressTriggeredRef.current) {
                  e.preventDefault();
                  longPressTriggeredRef.current = false;
                }
              }}
              onTouchStart={startMobileLogoHold}
              onTouchEnd={cancelMobileLogoHold}
              onTouchCancel={cancelMobileLogoHold}
              onContextMenu={(e) => {
                if (longPressTriggeredRef.current) {
                  e.preventDefault();
                  longPressTriggeredRef.current = false;
                }
              }}
            >
              <Logo className="h-8 w-auto fill-current text-black dark:text-white" />
            </Link>

            {/* Desktop Nav Items */}
            <div className="hidden md:flex items-center space-x-4">
              {navItems.map((item) => (
                <div key={item.name} className="relative group">
                  {item.path ? (
                    <Link
                      to={item.path}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                        location.pathname === item.path
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                      }`}
                    >
                      {item.icon}
                      {item.name}
                    </Link>
                  ) : (
                    <div
                      onMouseEnter={() => {
                        setActiveNavDropdown(item.name);
                        setUserDropdownOpen(false);
                      }}
                      onMouseLeave={() => setActiveNavDropdown(null)}
                      className="relative"
                    >
                      <button
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center transition-colors ${
                          activeNavDropdown === item.name
                            ? "text-blue-600 dark:text-blue-400"
                            : "text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                        }`}
                      >
                        {item.name}
                        <BsChevronDown
                          className={`ml-1 transition-transform ${
                            activeNavDropdown === item.name ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {activeNavDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute left-0 mt-2 w-56 rounded-lg shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 py-1 z-50 overflow-hidden"
                          >
                            {item.subItems.map((sub) => (
                              <Link
                                key={sub.name}
                                to={sub.path}
                                className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                              >
                                <motion.div whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 300 }}>
                                  {sub.name}
                                </motion.div>
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Auth */}
            <div className="hidden md:flex items-center space-x-4">
              {SHOW_LANGUAGE_SWITCH ? (
                <LanguageSelector variant="desktop" />
              ) : (
                <ThemeModeToggle variant="desktop" />
              )}
              {user ? (
                <div className="relative" ref={userDropdownRef}>
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  >
                    {logoURL ? (
                      <img src={logoURL} alt={businessName || "Business logo"} className={`w-8 h-8 rounded-full object-contain ${logoBackdropClass} p-1 border border-gray-200 dark:border-gray-700`} />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                        {(businessName || user.email || "").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="hidden lg:inline max-w-[12rem] truncate">{businessName || user.email.split("@")[0]}</span>
                    <BsChevronDown
                      className={`transition-transform ${userDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence>
                    {userDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 py-1 z-50"
                        style={{ transformOrigin: "top right" }}
                      >
                        <div className="px-4 py-3 bg-white dark:bg-gray-800 rounded-t-lg">
                          <div className="flex items-center gap-3 min-w-0">
                            {logoURL ? (
                              <img src={logoURL} alt={businessName || "Business logo"} className={`w-8 h-8 rounded-full object-contain ${logoBackdropClass} p-1 border border-gray-200 dark:border-gray-700`} />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                                {(businessName || user.email || "").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{businessName || user.email}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleNavigation("/myaccount")}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                        >
                          {t("auth.myAccount")}
                        </button>
                        <button
                          onClick={() => handleNavigation("/", "logout")}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center transition-colors"
                        >
                          <BsBoxArrowRight className="mr-2" /> {t("auth.signOut")}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => handleNavigation("/login")}
                    className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  >
                    {t("auth.login")}
                  </button>
                  <button
                    onClick={() => handleNavigation("/signup")}
                    className="font-display-4 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-sm transition-all hover:shadow-md"
                  >
                    {t("auth.getStarted")}
                  </button>
                </>
              )}
            </div>

            {/* Mobile Toggle */}
            <button
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white focus:outline-none"
              onClick={toggleMenu}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
            {menuOpen ? <BsX size={24} /> : <BsList size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <LayoutGroup>
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              key="mobile-menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-lg"
              onClick={toggleMenu}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
            >
              <motion.div
                className="relative mx-auto flex h-full w-full max-w-lg flex-col px-5 py-6"
                initial={{ y: -20, opacity: 0.85 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0.85 }}
                transition={{ type: "spring", stiffness: 240, damping: 26 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    className="flex items-center"
                    onClick={() => {
                      if (longPressTriggeredRef.current) {
                        longPressTriggeredRef.current = false;
                        return;
                      }
                      handleNavigation("/");
                    }}
                    onTouchStart={startMobileLogoHold}
                    onTouchEnd={cancelMobileLogoHold}
                    onTouchCancel={cancelMobileLogoHold}
                    onContextMenu={(e) => {
                      if (longPressTriggeredRef.current) {
                        e.preventDefault();
                        longPressTriggeredRef.current = false;
                      }
                    }}
                    aria-label="Go to homepage"
                  >
                    <Logo className="h-9 w-auto fill-current text-white" />
                  </button>
                  <motion.button
                    onClick={toggleMenu}
                    whileTap={{ scale: 0.9, rotate: 90 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.18 }}
                    aria-label="Close menu"
                    className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white backdrop-blur"
                  >
                    <BsX size={24} />
                  </motion.button>
                </div>

                <motion.div
                  className="mt-8 rounded-3xl border border-white/15 bg-white/85 p-6 shadow-2xl backdrop-blur-xl dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-100"
                  initial={{ y: -12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05, type: "spring", stiffness: 220, damping: 24 }}
                >
                  {user ? (
                    <>
                      <div className="flex items-center gap-4">
                        {logoURL ? (
                          <img
                            src={logoURL}
                            alt={businessName || "Business logo"}
                            className={`h-14 w-14 rounded-2xl object-contain ${logoBackdropClass} p-1 border border-white/60`}
                          />
                        ) : (
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-2xl font-semibold text-white">
                            {(businessName || user.email || "").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-lg font-semibold text-slate-900 dark:text-gray-100">
                            {businessName || user.email}
                          </p>
                          <p className="truncate text-sm text-slate-500 dark:text-gray-400">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                        <motion.button
                          type="button"
                          className="flex-1 rounded-2xl border border-white/50 bg-white/90 px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-white dark:border-gray-700/60 dark:bg-gray-800/80 dark:text-gray-100"
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleNavigation("/myaccount")}
                        >
                          {t("auth.myAccount")}
                        </motion.button>
                        <motion.button
                          type="button"
                          className="flex-1 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/40 transition hover:brightness-105"
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleNavigation("/", "logout")}
                        >
                          {t("auth.signOut")}
                        </motion.button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600/90 text-2xl font-semibold text-white">
                          <BsGlobe />
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-slate-900 dark:text-gray-100">Welcome to ZEDI</p>
                          <p className="text-sm text-slate-500 dark:text-gray-400">Sign in to keep your journey in sync.</p>
                        </div>
                      </div>
                      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                        <motion.button
                          type="button"
                          className="font-display-4 flex-1 rounded-2xl bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:brightness-105"
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleNavigation("/login")}
                        >
                          {t("auth.login")}
                        </motion.button>
                        <motion.button
                          type="button"
                          className="font-display-4 flex-1 rounded-2xl border border-white/40 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-white dark:border-gray-700/60 dark:bg-gray-800/80 dark:text-gray-100"
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleNavigation("/signup")}
                        >
                          {t("auth.getStartedCaps")}
                        </motion.button>
                      </div>
                    </>
                  )}
                </motion.div>

                <div className="mt-8 flex-1 overflow-y-auto pb-16">
                  <div className="space-y-2">
                    {navItems.map((item) => {
                      const isExpanded = expandedMobileItem === item.name;
                      const isActive = Boolean(item.path && location.pathname === item.path);
                      return (
                        <motion.button
                          key={item.name}
                          type="button"
                          className={`group flex w-full items-center justify-between rounded-2xl border border-white/15 px-5 py-4 text-left text-lg font-semibold transition ${
                            isActive
                              ? "bg-white/90 text-blue-700 shadow-lg shadow-blue-500/20"
                              : "bg-white/70 text-slate-900 hover:bg-white/85 dark:bg-gray-900/80 dark:text-gray-100 dark:hover:bg-gray-800"
                          } backdrop-blur-xl`}
                          whileHover={{ y: -1 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => {
                            if (item.subItems) {
                              setExpandedMobileItem(
                                expandedMobileItem === item.name ? null : item.name
                              );
                            } else {
                              handleNavigation(item.path);
                            }
                          }}
                        >
                          <span className="pr-6">{item.name}</span>
                          {item.subItems ? (
                            <motion.span
                              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-slate-600 shadow-sm dark:bg-gray-800/80 dark:text-gray-300"
                              animate={{ rotate: isExpanded ? 180 : 0 }}
                              transition={{ type: "spring", stiffness: 240, damping: 20 }}
                            >
                              <BsChevronDown size={18} />
                            </motion.span>
                          ) : (
                            <span className="text-xs uppercase tracking-[0.35em] text-blue-600/80 opacity-0 transition-opacity group-hover:opacity-100">Go</span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>

                  <AnimatePresence>
                    {expandedMobileItem && (
                      <motion.div
                        key="mobile-submenu"
                        className="mt-3 space-y-2 rounded-2xl border border-white/20 bg-white/85 p-4 shadow-inner backdrop-blur-xl dark:border-gray-800/70 dark:bg-gray-900/90"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                      >
                        {navItems
                          .find((n) => n.name === expandedMobileItem)
                          .subItems.map((sub) => (
                            <motion.button
                              key={sub.name}
                              type="button"
                              className="flex w-full items-center justify-between rounded-xl px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-white dark:text-gray-300 dark:hover:bg-gray-800"
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleNavigation(sub.path)}
                            >
                              <span>{sub.name}</span>
                              <span className="text-xs uppercase tracking-[0.3em] text-blue-500/70">Go</span>
                            </motion.button>
                          ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {SHOW_LANGUAGE_SWITCH ? (
                    <div className="mt-6">
                      <LanguageSelector variant="mobile" />
                    </div>
                  ) : (
                    <>
                      <ThemeModeToggle variant="mobile" />
                      <motion.button
                        type="button"
                        className="mt-3 w-full rounded-2xl border border-white/20 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-xl transition hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-700/70 dark:bg-gray-900/85 dark:hover:bg-gray-900"
                        whileTap={{ scale: 0.99 }}
                        onClick={openQrExperience}
                      >
                        <span className="flex items-center justify-between gap-4">
                          <span className="text-left">
                            <span className="block text-sm font-semibold text-slate-900 dark:text-gray-100">
                              Show QR
                            </span>
                            <span className="block text-xs text-slate-500 dark:text-gray-400">
                              Open the scan card for in-person sharing
                            </span>
                          </span>
                          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-700 shadow-sm dark:border-slate-700/80 dark:bg-slate-800 dark:text-slate-200">
                            <BsTablet className="h-4 w-4" />
                          </span>
                        </span>
                      </motion.button>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </LayoutGroup>

      <AnimatePresence>
        {showQrModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setShowQrModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowQrModal(false)}
                className="absolute right-4 top-4 text-gray-500 transition hover:text-gray-700"
                aria-label="Close QR experience"
              >
                <BsX size={24} className="text-neutral-900" />
              </button>

              <div className="text-center">
                <Logo className="mx-auto mb-3 h-6 w-auto text-gray-900" />
                <p className="mb-6 text-sm text-gray-600">Scan to open ZEDIPASS on mobile</p>

                <div className="inline-block rounded-xl bg-white p-4 shadow-inner">
                  <img
                    src="/zedipass-download-qr.png"
                    alt="QR code to download ZediPass from the correct app store"
                    className="mx-auto h-[180px] w-[180px]"
                  />
                  <p className="mt-2 break-all text-xs text-gray-500">
                    www.zedipass.com/get
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Controls */}
      <AnimatePresence>
        {!visible && hasScrolled && (
          <motion.button
            key="floating-menu"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 md:hidden p-4 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 transition-transform"
            onClick={toggleMenu}
            aria-label="Open menu"
            data-floating-menu="true"
          >
            <BsList size={24} />
          </motion.button>
        )}
        {showBackToTop && (
          <motion.button
            key="back-to-top"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="fixed bottom-8 right-6 z-50 hidden md:block p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700"
            aria-label="Back to top"
          >
            <BsArrowUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
